import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, authFetch } from "../App";
import { toast } from "sonner";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import {
  ArrowLeft, ListTodo, FileText, Info, Plus, Filter,
  CheckCircle2, Clock, Circle, MapPin, Phone, User,
  AlertOctagon, FileIcon, Download, Camera, Navigation,
  Layers, X, Image, Trash2, ChevronUp, ImagePlus, Save,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import ImpersonationBanner from "../components/ImpersonationBanner";

const API = "/api";

/* ───── Compress image before upload ───── */
const MAX_FILE_SIZE_MB = 10;
function compressImage(file, quality = 0.75, maxW = 1600) {
  return new Promise((resolve) => {
    if (!file.type.startsWith("image/") || file.type === "image/gif") { resolve(file); return; }
    const img = new window.Image();
    img.onload = () => {
      let w = img.width, h = img.height;
      if (w > maxW) { h = Math.round((h * maxW) / w); w = maxW; }
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => {
        resolve(blob ? new File([blob], file.name, { type: "image/jpeg" }) : file);
      }, "image/jpeg", quality);
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });
}

/* ───── Photo Source Picker (Camera vs Gallery) ───── */
function PhotoSourcePicker({ open, onClose, onCamera, onGallery }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[900] flex items-end justify-center" onClick={onClose} data-testid="photo-source-picker">
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative w-full max-w-md mb-safe pb-2 px-3 animate-in slide-in-from-bottom-4 duration-200" onClick={e => e.stopPropagation()}>
        <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-2xl">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-800 text-center">Foto hinzufuegen</p>
          </div>
          <button
            onClick={onCamera}
            className="flex items-center gap-3 w-full px-4 py-3.5 text-left hover:bg-gray-50 active:bg-gray-100 transition-colors"
            data-testid="photo-source-camera"
          >
            <div className="p-2 rounded-xl bg-blue-50">
              <Camera className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-800">Foto aufnehmen</p>
              <p className="text-[11px] text-gray-400">Kamera oeffnen</p>
            </div>
          </button>
          <button
            onClick={onGallery}
            className="flex items-center gap-3 w-full px-4 py-3.5 text-left hover:bg-gray-50 active:bg-gray-100 transition-colors border-t border-gray-100"
            data-testid="photo-source-gallery"
          >
            <div className="p-2 rounded-xl bg-emerald-50">
              <ImagePlus className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-800">Aus Galerie waehlen</p>
              <p className="text-[11px] text-gray-400">Mehrfachauswahl moeglich</p>
            </div>
          </button>
        </div>
        <button
          onClick={onClose}
          className="w-full mt-2 py-3 bg-white rounded-2xl text-sm font-semibold text-gray-500 hover:text-gray-700 border border-gray-200 transition-colors"
          data-testid="photo-source-cancel"
        >
          Abbrechen
        </button>
      </div>
    </div>
  );
}

/* ───── Fullscreen Photo Overlay ───── */
function PhotoOverlay({ src, onClose }) {
  if (!src) return null;
  return (
    <div className="fixed inset-0 z-[999] bg-black/95 flex items-center justify-center" onClick={onClose} data-testid="photo-overlay">
      <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-white/10 rounded-full text-white hover:bg-white/20 z-10">
        <X className="w-6 h-6" />
      </button>
      <img src={src} alt="Originalbild" className="max-w-full max-h-full object-contain" onClick={e => e.stopPropagation()} />
    </div>
  );
}

/* ───────────── Status Config ───────────── */
const STATUS_FLOW = ["offen", "in_arbeit", "erledigt"];
const STATUS_CONFIG = {
  offen:     { bg: "bg-gray-100", ring: "ring-gray-300", text: "text-gray-600", icon: Circle,       label: "Offen" },
  in_arbeit: { bg: "bg-amber-100", ring: "ring-amber-400", text: "text-amber-700", icon: Clock,      label: "In Arbeit" },
  erledigt:  { bg: "bg-green-100", ring: "ring-green-500", text: "text-green-700", icon: CheckCircle2, label: "Erledigt" },
  blockiert: { bg: "bg-red-100",  ring: "ring-red-400", text: "text-red-700", icon: AlertOctagon,  label: "Blockiert" },
};

/* ───────────── Progress Bar ───────────── */
function ProgressBar({ tasks }) {
  const total = tasks.length;
  const done = tasks.filter(t => t.status === "erledigt").length;
  const inProgress = tasks.filter(t => t.status === "in_arbeit").length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  if (total === 0) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="task-progress">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-gray-800">Fortschritt</span>
        <span className="text-sm font-bold text-gray-900">{done}/{total} erledigt</span>
      </div>
      <div className="h-3 bg-gray-100 rounded-full overflow-hidden flex">
        {done > 0 && (
          <div className="bg-green-500 transition-all duration-500" style={{ width: `${(done / total) * 100}%` }} />
        )}
        {inProgress > 0 && (
          <div className="bg-amber-400 transition-all duration-500" style={{ width: `${(inProgress / total) * 100}%` }} />
        )}
      </div>
      <div className="flex gap-4 mt-2 text-xs text-gray-500">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" />{done} erledigt</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" />{inProgress} in Arbeit</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-300" />{total - done - inProgress} offen</span>
      </div>
    </div>
  );
}

/* ───────────── WhatsApp-Style Task Card ───────────── */
function TaskCard({ task, onTapStatus, onSelect, canUploadPhoto, onPhotoClick }) {
  const cfg = STATUS_CONFIG[task.status] || STATUS_CONFIG.offen;
  const Icon = cfg.icon;

  const handleStatusTap = (e) => {
    e.stopPropagation();
    onTapStatus(task);
  };

  return (
    <div
      className="bg-white border border-gray-200 rounded-xl overflow-hidden active:scale-[0.99] transition-transform"
      data-testid={`task-card-${task.id}`}
    >
      <div className="flex">
        {/* Status Stripe – tap to cycle */}
        <button
          onClick={handleStatusTap}
          className={`w-14 flex-shrink-0 flex flex-col items-center justify-center gap-1 ${cfg.bg} border-r border-gray-200 active:opacity-70 transition-opacity`}
          data-testid={`status-tap-${task.id}`}
          title="Status wechseln"
        >
          <Icon className={`w-5 h-5 ${cfg.text}`} />
          <span className={`text-[9px] font-bold ${cfg.text} leading-none`}>{cfg.label}</span>
        </button>

        {/* Content */}
        <div className="flex-1 p-3 min-w-0" onClick={() => onSelect(task)}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] text-gray-400 font-mono">{task.task_number}</p>
              <h3 className="font-semibold text-gray-900 text-[15px] leading-snug line-clamp-2">{task.title}</h3>
            </div>
            {/* Photo indicator + camera */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {task.photos?.length > 0 && (
                <span className="flex items-center gap-0.5 text-[10px] text-gray-400">
                  <Image className="w-3 h-3" />{task.photos.length}
                </span>
              )}
              {canUploadPhoto && (
                <button
                  onClick={(e) => { e.stopPropagation(); onPhotoClick(task); }}
                  className="p-1.5 rounded-lg bg-gray-50 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                  data-testid={`photo-btn-${task.id}`}
                >
                  <Camera className="w-4 h-4 text-gray-500" />
                </button>
              )}
            </div>
          </div>
          {task.description && (
            <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">{task.description}</p>
          )}
          <div className="flex items-center gap-3 mt-2 text-[11px] text-gray-400">
            <span className="flex items-center gap-1">
              <User className="w-3 h-3" />
              {task.assigned_to_name || "Nicht zugewiesen"}
            </span>
            {task.due_date && (
              <span className={task.due_date < new Date().toISOString().split("T")[0] && task.status !== "erledigt" ? "text-red-500 font-medium" : ""}>
                {format(new Date(task.due_date), "dd.MM.", { locale: de })}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───────────── Task Detail Modal (Directly Editable) ───────────── */
function TaskDetailModal({ task: initialTask, open, onClose, onStatusChange, projectId, canUploadPhoto, onRefresh, users }) {
  const [task, setTask] = useState(initialTask);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [overlayPhoto, setOverlayPhoto] = useState(null);
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  // Sync task when initialTask changes
  useEffect(() => {
    if (initialTask) {
      setTask(initialTask);
      setEditForm({
        title: initialTask.title || "",
        description: initialTask.description || "",
        assigned_to: initialTask.assigned_to || "",
      });
      setHasChanges(false);
    }
  }, [initialTask]);

  if (!task) return null;

  const statusOpts = [
    { value: "offen", label: "Offen", color: "bg-gray-100 text-gray-700" },
    { value: "in_arbeit", label: "In Arbeit", color: "bg-amber-100 text-amber-700" },
    { value: "erledigt", label: "Erledigt", color: "bg-green-100 text-green-700" },
  ];

  const handleFieldChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleStatusClick = async (newStatus) => {
    const res = await authFetch(`${API}/projects/${projectId}/tasks/${task.id}`, {
      method: "PATCH",
      body: JSON.stringify({ status: newStatus }),
    });
    if (res.ok) {
      toast.success("Status aktualisiert");
      setTask(prev => ({ ...prev, status: newStatus }));
      onStatusChange(task.id, newStatus);
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Fehler beim Status-Update");
    }
  };

  const handleSave = async () => {
    if (!hasChanges) return;
    setSaving(true);
    const body = {};
    if (editForm.title !== task.title) body.title = editForm.title;
    if (editForm.description !== (task.description || "")) body.description = editForm.description;
    if (editForm.assigned_to !== (task.assigned_to || "")) body.assigned_to = editForm.assigned_to || null;
    if (Object.keys(body).length === 0) { setSaving(false); setHasChanges(false); return; }
    try {
      const res = await authFetch(`${API}/projects/${projectId}/tasks/${task.id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success("Aufgabe gespeichert");
        setHasChanges(false);
        onRefresh();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Fehler beim Speichern");
      }
    } catch {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
          toast.error(`${file.name} zu gross (max ${MAX_FILE_SIZE_MB} MB)`);
          continue;
        }
        const compressed = await compressImage(file, 0.75, 1600);
        const fd = new FormData();
        fd.append("file", compressed);
        const res = await authFetch(`${API}/projects/${projectId}/tasks/${task.id}/photos`, {
          method: "POST", body: fd, headers: {},
        });
        if (res.ok) {
          toast.success("Foto hochgeladen");
        } else {
          const err = await res.json().catch(() => ({}));
          toast.error(err.detail || "Upload fehlgeschlagen");
        }
      }
      onRefresh();
    } catch {
      toast.error("Upload fehlgeschlagen");
    } finally {
      setUploading(false);
      if (cameraRef.current) cameraRef.current.value = "";
      if (galleryRef.current) galleryRef.current.value = "";
    }
  };

  const handleDeletePhoto = async (photoId) => {
    const res = await authFetch(`${API}/projects/${projectId}/tasks/${task.id}/photos/${photoId}`, {
      method: "DELETE",
    });
    if (res.ok) {
      toast.success("Foto geloescht");
      setTask(prev => ({ ...prev, photos: (prev.photos || []).filter(p => p.id !== photoId) }));
      onRefresh();
    } else {
      toast.error("Fehler beim Loeschen");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      {overlayPhoto && <PhotoOverlay src={overlayPhoto} onClose={() => setOverlayPhoto(null)} />}
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-gray-400 text-sm font-mono">{task.task_number}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Editable Title */}
          <div>
            <Label className="text-xs text-gray-500">Titel</Label>
            <Input
              value={editForm.title}
              onChange={e => handleFieldChange("title", e.target.value)}
              className="mt-1 font-semibold text-base"
              data-testid="detail-edit-title"
            />
          </div>

          {/* Editable Description */}
          <div>
            <Label className="text-xs text-gray-500">Beschreibung</Label>
            <Textarea
              value={editForm.description}
              onChange={e => handleFieldChange("description", e.target.value)}
              rows={3}
              className="mt-1"
              placeholder="Beschreibung hinzufuegen..."
              data-testid="detail-edit-description"
            />
          </div>

          {/* Editable Assignment */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Zuweisen an</Label>
              <Select
                value={editForm.assigned_to || "none"}
                onValueChange={v => handleFieldChange("assigned_to", v === "none" ? "" : v)}
              >
                <SelectTrigger className="mt-1" data-testid="detail-edit-assign">
                  <SelectValue placeholder="Nicht zugewiesen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nicht zugewiesen</SelectItem>
                  {(users || []).map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Erstellt von</Label>
              <p className="font-medium mt-2.5 text-sm">{task.created_by_name || "Unbekannt"}</p>
            </div>
          </div>

          {/* Save Button - only when changes exist */}
          {hasChanges && (
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full gap-2"
              data-testid="detail-save-btn"
            >
              <Save className="w-4 h-4" />
              {saving ? "Speichern..." : "Aenderungen speichern"}
            </Button>
          )}

          {/* Status Buttons */}
          {task.can_edit !== false && (
            <div>
              <Label className="text-xs text-gray-500 mb-2 block">Status</Label>
              <div className="flex gap-2">
                {statusOpts.map(o => (
                  <button
                    key={o.value}
                    onClick={() => handleStatusClick(o.value)}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                      task.status === o.value ? `${o.color} ring-2 ring-offset-1 ring-gray-900` : `${o.color} opacity-50 hover:opacity-80`
                    }`}
                    data-testid={`detail-status-${o.value}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {task.due_date && (
            <div>
              <Label className="text-xs text-gray-500">Faellig am</Label>
              <p className="font-medium mt-0.5 text-sm">{format(new Date(task.due_date), "dd.MM.yyyy", { locale: de })}</p>
            </div>
          )}

          {/* Photos Section */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-xs text-gray-500 flex items-center gap-1.5">
                <Camera className="w-3.5 h-3.5" />
                Fotos {task.photos?.length > 0 && `(${task.photos.length})`}
              </Label>
              {/* Hidden file inputs OUTSIDE dialog portal */}
              <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoUpload} />
              <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} />
              {canUploadPhoto && (
                <button
                  onClick={() => setShowPhotoPicker(true)}
                  disabled={uploading}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold active:bg-blue-700 disabled:opacity-50 transition-colors"
                  data-testid="detail-photo-upload"
                >
                  <ImagePlus className="w-3.5 h-3.5" />
                  {uploading ? "Hochladen..." : "Foto hinzufuegen"}
                </button>
              )}
            </div>

            {task.photos?.length > 0 ? (
              <div className="grid grid-cols-3 gap-2">
                {task.photos.map((p) => (
                  <div key={p.id} className="relative group rounded-lg overflow-hidden border border-gray-200">
                    <img
                      src={p.url}
                      alt={p.filename}
                      className="w-full h-24 object-cover cursor-pointer"
                      onClick={() => setOverlayPhoto(p.url)}
                      data-testid={`detail-view-photo-${p.id}`}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <button
                        onClick={() => handleDeletePhoto(p.id)}
                        className="p-1.5 bg-red-500 rounded-lg text-white hover:bg-red-600"
                        data-testid={`detail-delete-photo-${p.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                <Camera className="w-6 h-6 text-gray-300 mx-auto mb-1" />
                <p className="text-xs text-gray-400">Noch keine Fotos</p>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Schliessen</Button>
        </DialogFooter>
      </DialogContent>
      <PhotoSourcePicker
        open={showPhotoPicker}
        onClose={() => setShowPhotoPicker(false)}
        onCamera={() => { setShowPhotoPicker(false); setTimeout(() => cameraRef.current?.click(), 100); }}
        onGallery={() => { setShowPhotoPicker(false); setTimeout(() => galleryRef.current?.click(), 100); }}
      />
    </Dialog>
  );
}

/* ───────────── Create Task Sheet ───────────── */
function CreateTaskSheet({ open, onClose, projectId, users, onCreated }) {
  const [mode, setMode] = useState("single"); // single | batch
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", assigned_to: "", priority: "normal" });
  const [batchForm, setBatchForm] = useState({ base_title: "", count: 10, assigned_to: "", priority: "normal" });

  const resetForms = () => {
    setForm({ title: "", description: "", assigned_to: "", priority: "normal" });
    setBatchForm({ base_title: "", count: 10, assigned_to: "", priority: "normal" });
    setMode("single");
  };

  const handleSingleCreate = async () => {
    if (!form.title.trim()) { toast.error("Titel ist erforderlich"); return; }
    setCreating(true);
    try {
      const res = await authFetch(`${API}/projects/${projectId}/tasks`, {
        method: "POST",
        body: JSON.stringify({
          project_id: projectId,
          title: form.title,
          description: form.description || "",
          assigned_to: form.assigned_to || null,
          priority: form.priority,
          status: "offen",
        }),
      });
      if (res.ok) {
        const t = await res.json();
        toast.success("Aufgabe erstellt");
        onCreated([t]);
        onClose();
        resetForms();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Fehler");
      }
    } catch { toast.error("Fehler"); } finally { setCreating(false); }
  };

  const handleBatchCreate = async () => {
    if (!batchForm.base_title.trim()) { toast.error("Name ist erforderlich"); return; }
    if (batchForm.count < 1 || batchForm.count > 100) { toast.error("Anzahl: 1-100"); return; }
    setCreating(true);
    try {
      const res = await authFetch(`${API}/projects/${projectId}/tasks/batch`, {
        method: "POST",
        body: JSON.stringify({
          base_title: batchForm.base_title,
          count: parseInt(batchForm.count),
          assigned_to: batchForm.assigned_to || null,
          priority: batchForm.priority,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.created} Aufgaben erstellt`);
        onCreated(data.tasks);
        onClose();
        resetForms();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Fehler");
      }
    } catch { toast.error("Fehler"); } finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { onClose(); resetForms(); } }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{mode === "single" ? "Neue Aufgabe" : "Mehrere Aufgaben erstellen"}</DialogTitle>
        </DialogHeader>

        {/* Mode Toggle */}
        <div className="flex rounded-xl bg-gray-100 p-1 gap-1">
          <button
            onClick={() => setMode("single")}
            className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${mode === "single" ? "bg-white shadow-sm text-gray-900" : "text-gray-500"}`}
            data-testid="mode-single"
          >
            Einzeln
          </button>
          <button
            onClick={() => setMode("batch")}
            className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-1.5 ${mode === "batch" ? "bg-white shadow-sm text-gray-900" : "text-gray-500"}`}
            data-testid="mode-batch"
          >
            <Layers className="w-3.5 h-3.5" />
            Schnellmodus
          </button>
        </div>

        {mode === "single" ? (
          <div className="space-y-3">
            <div>
              <Label>Titel *</Label>
              <Input
                value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                placeholder="z.B. Außenleuchte montieren"
                autoFocus data-testid="task-title-input"
              />
            </div>
            <div>
              <Label>Beschreibung</Label>
              <Textarea
                value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Optional" rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Zuweisen an</Label>
                <Select value={form.assigned_to} onValueChange={v => setForm(p => ({ ...p, assigned_to: v }))}>
                  <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priorität</Label>
                <Select value={form.priority} onValueChange={v => setForm(p => ({ ...p, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="niedrig">Niedrig</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="hoch">Hoch</SelectItem>
                    <SelectItem value="kritisch">Kritisch</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={handleSingleCreate} disabled={creating} className="w-full" data-testid="create-task-btn">
              {creating ? "Erstelle..." : "Aufgabe erstellen"}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
              <p className="text-sm text-blue-800 font-medium">Schnellmodus</p>
              <p className="text-xs text-blue-600 mt-0.5">Gib einen Namen und die Anzahl ein. Das System erstellt automatisch nummerierte Aufgaben.</p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label>Name *</Label>
                <Input
                  value={batchForm.base_title} onChange={e => setBatchForm(p => ({ ...p, base_title: e.target.value }))}
                  placeholder="z.B. Außenleuchte"
                  data-testid="batch-title-input"
                />
              </div>
              <div>
                <Label>Anzahl</Label>
                <Input
                  type="number" min={1} max={100}
                  value={batchForm.count} onChange={e => setBatchForm(p => ({ ...p, count: e.target.value }))}
                  data-testid="batch-count-input"
                />
              </div>
            </div>
            {batchForm.base_title && (
              <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500">
                Vorschau: {Array.from({ length: Math.min(3, parseInt(batchForm.count) || 0) }, (_, i) => `"${batchForm.base_title} ${i + 1}"`).join(", ")}
                {parseInt(batchForm.count) > 3 && ` ... "${batchForm.base_title} ${batchForm.count}"`}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Zuweisen an</Label>
                <Select value={batchForm.assigned_to} onValueChange={v => setBatchForm(p => ({ ...p, assigned_to: v }))}>
                  <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priorität</Label>
                <Select value={batchForm.priority} onValueChange={v => setBatchForm(p => ({ ...p, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="niedrig">Niedrig</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="hoch">Hoch</SelectItem>
                    <SelectItem value="kritisch">Kritisch</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={handleBatchCreate} disabled={creating} className="w-full gap-2" data-testid="create-batch-btn">
              <Layers className="w-4 h-4" />
              {creating ? "Erstelle..." : `${batchForm.count} Aufgaben erstellen`}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════ MAIN COMPONENT ═══════════════ */
export default function WorkerProjectView() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selectedTask, setSelectedTask] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const photoInputCameraRef = useRef(null);
  const photoInputGalleryRef = useRef(null);
  const [photoTask, setPhotoTask] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showCardPhotoPicker, setShowCardPhotoPicker] = useState(false);

  /* ── Load Data ── */
  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, tRes, uRes] = await Promise.all([
          authFetch(`${API}/projects/${projectId}/worker-view`),
          authFetch(`${API}/projects/${projectId}/worker-tasks?filter_type=all`),
          authFetch(`${API}/users/directory`),
        ]);
        if (pRes.ok) setProject(await pRes.json());
        else { toast.error("Projekt nicht gefunden"); navigate("/app"); return; }
        if (tRes.ok) { const d = await tRes.json(); setTasks(d.tasks || []); }
        if (uRes.ok) setUsers(await uRes.json());
      } catch { toast.error("Fehler beim Laden"); }
      finally { setLoading(false); }
    };
    load();
  }, [projectId, navigate]);

  /* ── Reload tasks ── */
  const reloadTasks = useCallback(async () => {
    const params = new URLSearchParams({ filter_type: filter });
    const res = await authFetch(`${API}/projects/${projectId}/worker-tasks?${params}`);
    if (res.ok) { const d = await res.json(); setTasks(d.tasks || []); }
  }, [projectId, filter]);

  useEffect(() => { if (project) reloadTasks(); }, [filter, reloadTasks, project]);

  /* ── WhatsApp-Style: Tap → cycle status ── */
  const handleTapStatus = async (task) => {
    const idx = STATUS_FLOW.indexOf(task.status);
    const next = STATUS_FLOW[(idx + 1) % STATUS_FLOW.length];

    // Optimistic update
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: next } : t));

    const res = await authFetch(`${API}/projects/${projectId}/tasks/${task.id}`, {
      method: "PATCH",
      body: JSON.stringify({ status: next }),
    });
    if (!res.ok) {
      // Revert on error
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: task.status } : t));
      toast.error("Status konnte nicht geändert werden");
    }
  };

  const handleStatusChange = (taskId, newStatus) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
    if (selectedTask?.id === taskId) setSelectedTask(prev => prev ? { ...prev, status: newStatus } : null);
  };

  const handleTasksCreated = (newTasks) => {
    setTasks(prev => [...newTasks, ...prev]);
  };

  /* ── Photo Upload from Card ── */
  const handlePhotoFromCard = (task) => {
    setPhotoTask(task);
    setShowCardPhotoPicker(true);
  };

  const handlePhotoFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !photoTask) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
          toast.error(`${file.name} zu gross (max ${MAX_FILE_SIZE_MB} MB)`);
          continue;
        }
        const compressed = await compressImage(file, 0.75, 1600);
        const fd = new FormData();
        fd.append("file", compressed);
        const res = await authFetch(`${API}/projects/${projectId}/tasks/${photoTask.id}/photos`, {
          method: "POST", body: fd, headers: {},
        });
        if (res.ok) { toast.success("Foto hochgeladen"); }
        else { const err = await res.json().catch(() => ({})); toast.error(err.detail || "Upload fehlgeschlagen"); }
      }
      reloadTasks();
    } catch { toast.error("Upload fehlgeschlagen"); }
    finally {
      setUploading(false);
      setPhotoTask(null);
      if (photoInputCameraRef.current) photoInputCameraRef.current.value = "";
      if (photoInputGalleryRef.current) photoInputGalleryRef.current.value = "";
    }
  };

  /* ── Filtered Tasks ── */
  const filteredTasks = tasks.filter(t => {
    if (filter === "all") return true;
    if (filter === "mine") return t.assigned_to === user?.id || t.created_by === user?.id;
    return t.status === filter;
  });

  const permissions = project?.user_permissions || {};
  const canCreate = permissions.can_create_tasks || permissions.is_admin;
  const canUploadPhoto = permissions.can_upload_photos || permissions.is_admin;

  if (loading) return (
    <div className="flex justify-center py-16">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
    </div>
  );

  if (!project) return <div className="text-center py-16 text-gray-500">Projekt nicht gefunden</div>;

  const googleMapsUrl = project.site_address
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(project.site_address)}`
    : null;

  /* ── Filter Buttons ── */
  const filterOptions = [
    { value: "all", label: "Alle" },
    { value: "mine", label: "Meine" },
    { value: "offen", label: "Offen" },
    { value: "in_arbeit", label: "In Arbeit" },
    { value: "erledigt", label: "Erledigt" },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <ImpersonationBanner />
      <input ref={photoInputCameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoFile} />
      <input ref={photoInputGalleryRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoFile} />

      <div className="pb-24 px-4 md:px-6" data-testid="worker-project-view">
        {/* ── Header ── */}
        <div className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200 -mx-4 px-4 py-3 md:-mx-6 md:px-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/app/workspace")} className="p-2 -ml-2 hover:bg-gray-100 rounded-lg" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="font-bold text-gray-900 truncate text-lg">{project.name}</h1>
              <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${project.status === "aktiv" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                {project.status === "aktiv" ? "Aktiv" : project.status}
              </span>
            </div>
            <button onClick={() => setShowInfo(v => !v)} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="info-btn">
              <Info className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="mt-4 space-y-4">
          {/* ── Navigation starten ── */}
          {googleMapsUrl && (
            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-blue-600 text-white rounded-xl px-4 py-3.5 active:bg-blue-700 transition-colors shadow-sm"
              data-testid="navigate-btn"
            >
              <Navigation className="w-5 h-5" />
              <div>
                <p className="font-semibold text-sm">Navigation starten</p>
                <p className="text-[11px] text-blue-200 line-clamp-1">{project.site_address}</p>
              </div>
            </a>
          )}

          {/* ── Progress ── */}
          <ProgressBar tasks={tasks} />

          {/* ── Aufgaben Header ── */}
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
              <ListTodo className="w-4 h-4" />
              Aufgaben
              <span className="text-xs font-normal text-gray-400">({tasks.length})</span>
            </h2>
          </div>

          {/* ── Filter Pills ── */}
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 scrollbar-hide">
            {filterOptions.map(f => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  filter === f.value ? "bg-gray-900 text-white" : "bg-white text-gray-600 border border-gray-200"
                }`}
                data-testid={`filter-${f.value}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* ── Task List ── */}
          <div className="space-y-2.5">
            {filteredTasks.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <ListTodo className="w-10 h-10 mx-auto mb-2 text-gray-200" />
                <p className="text-sm">Keine Aufgaben {filter !== "all" ? "mit diesem Filter " : ""}gefunden</p>
                {canCreate && (
                  <Button variant="outline" size="sm" className="mt-3 gap-1.5" onClick={() => setShowCreate(true)}>
                    <Plus className="w-3.5 h-3.5" />Aufgabe erstellen
                  </Button>
                )}
              </div>
            ) : (
              filteredTasks.map(task => (
                <TaskCard
                  key={task.id}
                  task={task}
                  onTapStatus={handleTapStatus}
                  onSelect={setSelectedTask}
                  canUploadPhoto={canUploadPhoto}
                  onPhotoClick={handlePhotoFromCard}
                />
              ))
            )}
          </div>

          {/* ── Info Section (collapsible) ── */}
          {showInfo && (
            <div className="space-y-3 border-t border-gray-200 pt-4 mt-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                  <Info className="w-4 h-4" />Projekt-Info
                </h3>
                <button onClick={() => setShowInfo(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
              </div>
              {project.site_address && (
                <div className="bg-white rounded-xl border p-3">
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><MapPin className="w-3 h-3" />Adresse</div>
                  <p className="text-sm text-gray-900">{project.site_address}</p>
                </div>
              )}
              {(project.contact_person || project.contact_phone) && (
                <div className="bg-white rounded-xl border p-3">
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><User className="w-3 h-3" />Ansprechpartner</div>
                  {project.contact_person && <p className="text-sm font-medium">{project.contact_person}</p>}
                  {project.contact_phone && (
                    <a href={`tel:${project.contact_phone}`} className="flex items-center gap-1 text-blue-600 text-sm mt-0.5">
                      <Phone className="w-3 h-3" />{project.contact_phone}
                    </a>
                  )}
                </div>
              )}
              {project.safety_notes && (
                <div className="bg-red-50 rounded-xl border border-red-200 p-3">
                  <div className="flex items-center gap-2 text-red-600 text-xs mb-1"><AlertOctagon className="w-3 h-3" />Sicherheitshinweise</div>
                  <p className="text-sm text-red-800 whitespace-pre-line">{project.safety_notes}</p>
                </div>
              )}
              {project.documents?.length > 0 && (
                <div className="bg-white rounded-xl border p-3">
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-2"><FileIcon className="w-3 h-3" />Dokumente</div>
                  {project.documents.map((d, i) => (
                    <a key={i} href={d.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 py-1.5 text-sm text-blue-600 hover:underline">
                      <Download className="w-3 h-3" />{d.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── FAB: + Aufgabe ── */}
      {canCreate && (
        <button
          onClick={() => setShowCreate(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-gray-900 text-white rounded-2xl shadow-lg flex items-center justify-center hover:bg-gray-800 active:bg-gray-700 transition-all z-20"
          data-testid="fab-create-task"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}

      {/* ── Uploading Overlay ── */}
      {uploading && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 flex items-center gap-3 shadow-xl">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900" />
            <span className="text-sm font-medium">Foto wird hochgeladen...</span>
          </div>
        </div>
      )}

      {/* ── Modals ── */}
      <TaskDetailModal
        task={selectedTask}
        open={!!selectedTask}
        onClose={() => setSelectedTask(null)}
        onStatusChange={handleStatusChange}
        projectId={projectId}
        canUploadPhoto={canUploadPhoto}
        onRefresh={() => { reloadTasks(); setSelectedTask(null); }}
        users={users}
      />
      <CreateTaskSheet
        open={showCreate}
        onClose={() => setShowCreate(false)}
        projectId={projectId}
        users={users}
        onCreated={handleTasksCreated}
      />
      <PhotoSourcePicker
        open={showCardPhotoPicker}
        onClose={() => { setShowCardPhotoPicker(false); setPhotoTask(null); }}
        onCamera={() => { setShowCardPhotoPicker(false); setTimeout(() => photoInputCameraRef.current?.click(), 100); }}
        onGallery={() => { setShowCardPhotoPicker(false); setTimeout(() => photoInputGalleryRef.current?.click(), 100); }}
      />
    </div>
  );
}
